import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, CheckCircle2, XCircle, Save, ShieldCheck, Plus, Trash2, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const STATUS_CLS: Record<string, string> = {
  CONTENT_REQUIRED: "bg-amber-50 text-amber-700",
  DRAFT: "bg-blue-50 text-blue-700",
  APPROVED: "bg-emerald-50 text-emerald-700",
};

const Badge = ({ s }: { s: string }) => (
  <span className={`text-[10px] font-mono-v font-semibold px-1.5 py-0.5 rounded ${STATUS_CLS[s] || "bg-zinc-100 text-zinc-500"}`}>{s}</span>
);

const Field = ({ label, value, onChange, area = false, testid }: any) => (
  <label className="block text-xs">
    <span className="font-medium text-muted-foreground">{label}</span>
    {area ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4}
        className="mt-1 w-full px-2.5 py-2 border border-border rounded-md text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
    ) : (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-2.5 py-2 border border-border rounded-md text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
    )}
  </label>
);

function ModuleEditor({ courseId, versionNo, mod, fr, isSuper, onSaved }: any) {
  const [f, setF] = useState<any>({
    title_fr: mod.title_fr, title_en: mod.title_en,
    objective_fr: mod.objective_fr, objective_en: mod.objective_en,
    content_fr: mod.content_fr, content_en: mod.content_en,
    reflection_fr: mod.reflection_fr, reflection_en: mod.reflection_en,
    video_url: mod.video?.url || "", order: mod.order,
  });
  const set = (k: string) => (v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put(`/admin/editor/courses/${courseId}/versions/${versionNo}/modules/${mod.id}`,
        { ...f, order: Number(f.order) || mod.order });
      toast.success(fr ? "Module enregistré" : "Module saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (status: string) => {
    try {
      await api.post(`/admin/editor/courses/${courseId}/versions/${versionNo}/modules/${mod.id}/status`, { status });
      toast.success(fr ? `Statut : ${status}` : `Status: ${status}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 space-y-2.5" data-testid={`module-editor-${mod.order}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Field label="Titre FR" value={f.title_fr} onChange={set("title_fr")} testid={`me-title-fr-${mod.order}`} />
        <Field label="Title EN" value={f.title_en} onChange={set("title_en")} testid={`me-title-en-${mod.order}`} />
        <Field label={fr ? "Objectif pédagogique FR" : "Learning objective FR"} value={f.objective_fr} onChange={set("objective_fr")} area testid={`me-obj-fr-${mod.order}`} />
        <Field label={fr ? "Objectif pédagogique EN" : "Learning objective EN"} value={f.objective_en} onChange={set("objective_en")} area testid={`me-obj-en-${mod.order}`} />
        <Field label={fr ? "Contenu FR" : "Content FR"} value={f.content_fr} onChange={set("content_fr")} area testid={`me-content-fr-${mod.order}`} />
        <Field label={fr ? "Contenu EN" : "Content EN"} value={f.content_en} onChange={set("content_en")} area testid={`me-content-en-${mod.order}`} />
        <Field label={fr ? "Réflexion / exercice FR" : "Reflection / exercise FR"} value={f.reflection_fr} onChange={set("reflection_fr")} area testid={`me-refl-fr-${mod.order}`} />
        <Field label={fr ? "Réflexion / exercice EN" : "Reflection / exercise EN"} value={f.reflection_en} onChange={set("reflection_en")} area testid={`me-refl-en-${mod.order}`} />
        <Field label={fr ? "URL vidéo (placeholder si vide)" : "Video URL (placeholder if empty)"} value={f.video_url} onChange={set("video_url")} testid={`me-video-${mod.order}`} />
        <Field label={fr ? "Ordre" : "Order"} value={f.order} onChange={set("order")} testid={`me-order-${mod.order}`} />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={save} data-testid={`me-save-${mod.order}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] transition-colors duration-200">
          <Save size={12} /> {fr ? "Enregistrer" : "Save"}
        </button>
        {isSuper && mod.status !== "APPROVED" && (
          <button onClick={() => setStatus("APPROVED")} data-testid={`me-approve-${mod.order}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-700 text-white px-3 py-1.5 rounded-md hover:bg-emerald-800 transition-colors duration-200">
            <ShieldCheck size={12} /> {fr ? "Approuver (contenu officiel)" : "Approve (official content)"}
          </button>
        )}
        {isSuper && mod.status === "APPROVED" && (
          <button onClick={() => setStatus("DRAFT")} data-testid={`me-to-draft-${mod.order}`}
            className="text-xs font-medium border border-border px-3 py-1.5 rounded-md hover:bg-muted transition-colors duration-200">
            {fr ? "Repasser en DRAFT" : "Back to DRAFT"}
          </button>
        )}
        <span className="text-[10px] text-muted-foreground font-mono-v">
          FR: {mod.locale_state?.fr === "COMPLETE" ? "✓ COMPLET" : "✗ INCOMPLET"} · EN: {mod.locale_state?.en === "COMPLETE" ? "✓ COMPLETE" : "✗ INCOMPLETE"}
        </span>
      </div>
    </div>
  );
}

const EMPTY_Q = { type: "SINGLE_CHOICE", text_fr: "", text_en: "", competency: "general", feedback_fr: "", feedback_en: "", options: [{ id: "a", text_fr: "", text_en: "" }, { id: "b", text_fr: "", text_en: "" }], correct_option_ids: [] as string[] };

function QuestionForm({ initial, fr, onSubmit, onCancel }: any) {
  const [q, setQ] = useState<any>(initial || EMPTY_Q);
  const set = (k: string) => (v: any) => setQ((p: any) => ({ ...p, [k]: v }));
  const setOpt = (i: number, k: string, v: string) => setQ((p: any) => ({ ...p, options: p.options.map((o: any, j: number) => j === i ? { ...o, [k]: v } : o) }));
  const toggleCorrect = (id: string) => setQ((p: any) => {
    const single = p.type !== "MULTIPLE_CHOICE";
    const has = p.correct_option_ids.includes(id);
    return { ...p, correct_option_ids: single ? [id] : has ? p.correct_option_ids.filter((x: string) => x !== id) : [...p.correct_option_ids, id] };
  });
  const setType = (t: string) => setQ((p: any) => t === "TRUE_FALSE"
    ? { ...p, type: t, options: [{ id: "true", text_fr: "Vrai", text_en: "True" }, { id: "false", text_fr: "Faux", text_en: "False" }], correct_option_ids: [] }
    : { ...p, type: t, correct_option_ids: p.type === "MULTIPLE_CHOICE" || t !== "MULTIPLE_CHOICE" ? p.correct_option_ids.slice(0, 1) : p.correct_option_ids });
  return (
    <div className="mt-3 border border-border rounded-md p-4 space-y-2.5 bg-muted/30" data-testid="question-form">
      <div className="flex gap-2 items-center">
        <select data-testid="qf-type" value={q.type} onChange={(e) => setType(e.target.value)}
          className="px-2 py-1.5 border border-border rounded-md text-xs bg-white">
          <option value="SINGLE_CHOICE">{fr ? "Choix simple" : "Single choice"}</option>
          <option value="MULTIPLE_CHOICE">{fr ? "Choix multiples" : "Multiple choice"}</option>
          <option value="TRUE_FALSE">{fr ? "Vrai / Faux" : "True / False"}</option>
        </select>
        <input data-testid="qf-competency" value={q.competency} onChange={(e) => set("competency")(e.target.value)}
          placeholder={fr ? "Compétence" : "Competency"} className="px-2 py-1.5 border border-border rounded-md text-xs flex-1" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Field label="Question FR" value={q.text_fr} onChange={set("text_fr")} area testid="qf-text-fr" />
        <Field label="Question EN" value={q.text_en} onChange={set("text_en")} area testid="qf-text-en" />
        <Field label={fr ? "Feedback compétence FR" : "Competency feedback FR"} value={q.feedback_fr} onChange={set("feedback_fr")} testid="qf-fb-fr" />
        <Field label={fr ? "Feedback compétence EN" : "Competency feedback EN"} value={q.feedback_en} onChange={set("feedback_en")} testid="qf-fb-en" />
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">{fr ? "Options (cocher = réponse correcte)" : "Options (check = correct answer)"}</p>
        {q.options.map((o: any, i: number) => (
          <div key={o.id} className="flex gap-2 items-center">
            <input type="checkbox" data-testid={`qf-correct-${o.id}`} checked={q.correct_option_ids.includes(o.id)} onChange={() => toggleCorrect(o.id)} />
            <span className="text-[10px] font-mono-v w-8">{o.id}</span>
            <input data-testid={`qf-opt-fr-${o.id}`} value={o.text_fr} onChange={(e) => setOpt(i, "text_fr", e.target.value)} placeholder="FR"
              className="flex-1 px-2 py-1.5 border border-border rounded-md text-xs" disabled={q.type === "TRUE_FALSE"} />
            <input data-testid={`qf-opt-en-${o.id}`} value={o.text_en} onChange={(e) => setOpt(i, "text_en", e.target.value)} placeholder="EN"
              className="flex-1 px-2 py-1.5 border border-border rounded-md text-xs" disabled={q.type === "TRUE_FALSE"} />
          </div>
        ))}
        {q.type !== "TRUE_FALSE" && (
          <button data-testid="qf-add-option" onClick={() => setQ((p: any) => ({ ...p, options: [...p.options, { id: String.fromCharCode(97 + p.options.length), text_fr: "", text_en: "" }] }))}
            className="text-xs text-[#002BF6]">+ {fr ? "Option" : "Option"}</button>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSubmit(q)} data-testid="qf-submit"
          className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] transition-colors duration-200">
          {fr ? "Enregistrer la question" : "Save question"}
        </button>
        <button onClick={onCancel} data-testid="qf-cancel" className="text-xs border border-border px-3 py-1.5 rounded-md hover:bg-muted">{fr ? "Annuler" : "Cancel"}</button>
      </div>
    </div>
  );
}

export const CourseEditor = ({ courseId, versionNo, isSuper, onClose }: any) => {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [data, setData] = useState<any>(null);
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [qForm, setQForm] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [newMod, setNewMod] = useState<any>(null);

  const addModule = async () => {
    try {
      await api.post(`/admin/editor/courses/${courseId}/versions/${versionNo}/modules`, newMod);
      toast.success(fr ? "Module créé" : "Module created");
      setNewMod(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delModule = async (mid: string) => {
    try {
      await api.delete(`/admin/editor/courses/${courseId}/versions/${versionNo}/modules/${mid}`);
      toast.success(fr ? "Module supprimé" : "Module deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(() => {
    api.get(`/admin/editor/courses/${courseId}/versions/${versionNo}`)
      .then((r) => {
        setData(r.data);
        setSettings({ pass_pct: r.data.version.quiz?.pass_pct ?? 70, max_attempts: r.data.version.quiz?.max_attempts ?? 3 });
      })
      .catch((e) => toast.error(apiError(e)));
  }, [courseId, versionNo]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { course, version, checklist, editable } = data;
  const questions = version.quiz?.questions || [];

  const saveSettings = async () => {
    try {
      await api.put(`/admin/editor/courses/${courseId}/versions/${versionNo}/quiz`,
        { pass_pct: Number(settings.pass_pct), max_attempts: Number(settings.max_attempts) });
      toast.success(fr ? "Paramètres du quiz enregistrés" : "Quiz settings saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const submitQuestion = async (q: any) => {
    try {
      if (q.id) await api.put(`/admin/editor/courses/${courseId}/versions/${versionNo}/quiz/questions/${q.id}`, q);
      else await api.post(`/admin/editor/courses/${courseId}/versions/${versionNo}/quiz/questions`, q);
      toast.success(fr ? "Question enregistrée" : "Question saved");
      setQForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const qAction = async (method: string, path: string, body?: any, msg?: string) => {
    try {
      await (api as any)[method](`/admin/editor/courses/${courseId}/versions/${versionNo}/quiz/questions/${path}`, body);
      toast.success(msg || "OK"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doPublish = async () => {
    try {
      await api.post(`/admin/courses/${courseId}/versions/${versionNo}/publish`);
      toast.success(fr ? "Version publiée" : "Version published");
      setConfirmPublish(false); onClose(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[55] overflow-y-auto p-4" data-testid="course-editor">
      <div className="bg-white rounded-md max-w-4xl mx-auto my-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{fr ? "Éditeur de cours" : "Course editor"}</p>
            <h2 className="font-heading text-xl font-semibold mt-1">{fr ? course.title_fr : course.title_en} — v{versionNo}</h2>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono-v">{version.status} · {course.data_class}</p>
          </div>
          <button onClick={() => onClose(false)} data-testid="editor-close" className="p-1.5 rounded-md hover:bg-muted"><X size={18} /></button>
        </div>
        {!editable && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="editor-immutable-notice">
            {fr ? "Version publiée — immuable. Créez une nouvelle version pour modifier." : "Published version — immutable. Create a new version to edit."}
          </p>
        )}

        {/* Modules */}
        <p className="mt-5 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Modules</p>
        <div className="mt-2 space-y-2">
          {version.modules.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="modules-empty-state">
              {fr ? "Aucun module — ajoutez un module pour commencer (requis pour la publication)." : "No modules — add a module to start (required for publication)."}
            </p>
          )}
          {version.modules.map((m: any) => (
            <div key={m.id} className="border border-border rounded-md p-3" data-testid={`editor-module-${m.order}`}>
              <div className="w-full flex items-center justify-between gap-2">
                <button className="flex-1 flex items-center justify-between gap-2 text-left" onClick={() => setOpenModule(openModule === m.id ? null : m.id)} data-testid={`editor-module-toggle-${m.order}`}>
                  <span className="text-sm font-medium">{m.order}. {fr ? m.title_fr : m.title_en}</span>
                  <span className="flex items-center gap-1.5">
                    <Badge s={m.status || "DRAFT"} />
                    <span className="text-[9px] font-mono-v text-muted-foreground">FR:{m.locale_state?.fr === "COMPLETE" ? "✓" : "✗"} EN:{m.locale_state?.en === "COMPLETE" ? "✓" : "✗"}</span>
                  </span>
                </button>
                {editable && (
                  <button onClick={() => { if (window.confirm(fr ? "Supprimer ce module ?" : "Delete this module?")) delModule(m.id); }}
                    data-testid={`module-delete-${m.order}`} className="p-1 rounded text-[#FF3B30] hover:bg-red-50" title={fr ? "Supprimer" : "Delete"}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              {openModule === m.id && editable && (
                <ModuleEditor courseId={courseId} versionNo={versionNo} mod={m} fr={fr} isSuper={isSuper} onSaved={load} />
              )}
            </div>
          ))}
        </div>
        {editable && !newMod && (
          <button onClick={() => setNewMod({ title_fr: "", title_en: "", type: "TEXT" })} data-testid="module-add-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium border border-[#002BF6] text-[#002BF6] px-3 py-1.5 rounded-md hover:bg-[#002BF6]/5 transition-colors duration-200">
            <Plus size={12} /> {fr ? "Ajouter un module" : "Add module"}
          </button>
        )}
        {newMod && (
          <div className="mt-2 border border-border rounded-md p-3 flex flex-wrap items-end gap-2 bg-muted/30" data-testid="module-add-form">
            <input data-testid="nm-title-fr" value={newMod.title_fr} onChange={(e) => setNewMod({ ...newMod, title_fr: e.target.value })}
              placeholder={fr ? "Titre FR" : "Title FR"} className="px-2 py-1.5 border border-border rounded-md text-xs flex-1 min-w-[140px]" />
            <input data-testid="nm-title-en" value={newMod.title_en} onChange={(e) => setNewMod({ ...newMod, title_en: e.target.value })}
              placeholder="Title EN" className="px-2 py-1.5 border border-border rounded-md text-xs flex-1 min-w-[140px]" />
            <select data-testid="nm-type" value={newMod.type} onChange={(e) => setNewMod({ ...newMod, type: e.target.value })}
              className="px-2 py-1.5 border border-border rounded-md text-xs bg-white">
              <option value="TEXT">TEXT</option><option value="VIDEO">VIDEO</option>
              <option value="REFLECTION">REFLECTION</option><option value="PRACTICE">PRACTICE</option>
            </select>
            <button onClick={addModule} data-testid="nm-submit" disabled={!newMod.title_fr.trim() || !newMod.title_en.trim()}
              className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] disabled:opacity-50">{fr ? "Créer" : "Create"}</button>
            <button onClick={() => setNewMod(null)} data-testid="nm-cancel" className="text-xs border border-border px-3 py-1.5 rounded-md hover:bg-muted">{fr ? "Annuler" : "Cancel"}</button>
          </div>
        )}

        {/* Quiz */}
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Quiz</p>
        {editable && settings && (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs"><span className="text-muted-foreground">{fr ? "Seuil de réussite %" : "Passing score %"}</span>
              <input data-testid="quiz-pass-pct" type="number" value={settings.pass_pct} onChange={(e) => setSettings({ ...settings, pass_pct: e.target.value })}
                className="mt-1 block w-24 px-2 py-1.5 border border-border rounded-md text-sm" /></label>
            <label className="text-xs"><span className="text-muted-foreground">{fr ? "Tentatives max" : "Max attempts"}</span>
              <input data-testid="quiz-max-attempts" type="number" value={settings.max_attempts} onChange={(e) => setSettings({ ...settings, max_attempts: e.target.value })}
                className="mt-1 block w-24 px-2 py-1.5 border border-border rounded-md text-sm" /></label>
            <button onClick={saveSettings} data-testid="quiz-settings-save"
              className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-2 rounded-md hover:bg-[#132c5c] transition-colors duration-200">{fr ? "Enregistrer" : "Save"}</button>
          </div>
        )}
        <div className="mt-3 space-y-2">
          {questions.map((q: any) => (
            <div key={q.id} className={`border border-border rounded-md p-3 ${q.active === false ? "opacity-50" : ""}`} data-testid={`editor-question-${q.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm">{fr ? q.text_fr : q.text_en} <span className="text-[10px] font-mono-v text-muted-foreground">({q.type} · {q.competency})</span></span>
                <span className="flex items-center gap-1.5">
                  <Badge s={q.status || "APPROVED"} />
                  {q.active === false && <span className="text-[10px] font-mono-v text-zinc-500">INACTIVE</span>}
                </span>
              </div>
              {editable && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => setQForm(q)} data-testid={`q-edit-${q.id}`} className="text-xs border border-border px-2.5 py-1 rounded-md hover:bg-muted">{fr ? "Modifier" : "Edit"}</button>
                  {isSuper && (q.status || "DRAFT") !== "APPROVED" && (
                    <button onClick={() => qAction("post", `${q.id}/status`, { status: "APPROVED" }, fr ? "Question approuvée" : "Question approved")} data-testid={`q-approve-${q.id}`}
                      className="text-xs bg-emerald-700 text-white px-2.5 py-1 rounded-md hover:bg-emerald-800">{fr ? "Approuver" : "Approve"}</button>
                  )}
                  {q.active !== false ? (
                    <button onClick={() => qAction("delete", q.id, undefined, fr ? "Question désactivée" : "Question deactivated")} data-testid={`q-deactivate-${q.id}`}
                      className="text-xs text-[#FF3B30] border border-red-200 px-2.5 py-1 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={11} /> {fr ? "Désactiver" : "Deactivate"}</button>
                  ) : (
                    <button onClick={() => qAction("put", q.id, { active: true }, fr ? "Question réactivée" : "Question reactivated")} data-testid={`q-reactivate-${q.id}`}
                      className="text-xs border border-border px-2.5 py-1 rounded-md hover:bg-muted">{fr ? "Réactiver" : "Reactivate"}</button>
                  )}
                </div>
              )}
            </div>
          ))}
          {questions.length === 0 && <p className="text-xs text-muted-foreground">{fr ? "Aucune question — CONTENT_REQUIRED." : "No questions — CONTENT_REQUIRED."}</p>}
        </div>
        {editable && !qForm && (
          <button onClick={() => setQForm({ ...EMPTY_Q })} data-testid="q-add-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium border border-[#002BF6] text-[#002BF6] px-3 py-1.5 rounded-md hover:bg-[#002BF6]/5 transition-colors duration-200">
            <Plus size={12} /> {fr ? "Ajouter une question" : "Add question"}
          </button>
        )}
        {qForm && <QuestionForm initial={qForm.id ? qForm : null} fr={fr} onSubmit={submitQuestion} onCancel={() => setQForm(null)} />}

        {/* Publication */}
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{fr ? "Publication (fail-closed)" : "Publication (fail-closed)"}</p>
        <div className="mt-2 space-y-1" data-testid="publish-checklist">
          {checklist.checks.map((c: any) => (
            <div key={c.key} className="flex items-start gap-2 text-xs" data-testid={`check-${c.key}`}>
              {c.ok ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={14} className="text-[#FF3B30] shrink-0 mt-0.5" />}
              <span><span className="font-mono-v font-semibold">{c.key}</span> — <span className="text-muted-foreground">{c.detail}</span></span>
            </div>
          ))}
        </div>
        {editable && isSuper && (
          <button onClick={() => setConfirmPublish(true)} disabled={!checklist.can_publish} data-testid="editor-publish-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium bg-[#002BF6] text-white px-4 py-2 rounded-md hover:bg-[#0024cc] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <Rocket size={14} /> {fr ? "Publier cette version" : "Publish this version"}
          </button>
        )}
        {!isSuper && <p className="mt-3 text-[11px] text-muted-foreground">{fr ? "Publication et approbation réservées à l'Owner / SUPER_ADMIN." : "Publication and approval restricted to Owner / SUPER_ADMIN."}</p>}

        {confirmPublish && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="publish-confirm-modal">
            <div className="bg-white rounded-md p-6 w-full max-w-md">
              <h3 className="font-heading font-semibold text-lg">{fr ? "Confirmer la publication" : "Confirm publication"}</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {fr ? "La version publiée devient immuable. Les apprenants inscrits restent épinglés à leur version. Ask Vytali n'est PAS activé automatiquement."
                    : "The published version becomes immutable. Enrolled learners stay pinned to their version. Ask Vytali is NOT enabled automatically."}
              </p>
              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={() => setConfirmPublish(false)} data-testid="publish-cancel" className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted">{fr ? "Annuler" : "Cancel"}</button>
                <button onClick={doPublish} data-testid="publish-confirm" className="px-4 py-2 text-sm bg-[#002BF6] text-white rounded-md hover:bg-[#0024cc]">{fr ? "Publier" : "Publish"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
